import type { components } from "@cntrl/api-client";

type ShowSummary = components["schemas"]["ShowSummary"];

/**
 * The Bandsintown for Artists bulk-upload template, column for column (artists.bandsintown.com/support/event-upload).
 * The band downloads this file and drops it on Events › Bulk Upload themselves — we never call Bandsintown (its
 * terms forbid commercial use of its data and it has no public API for creating events), so the CSV is the whole
 * integration. Header names and order are THEIRS; a renamed or reordered column is a rejected file.
 */
export const BANDSINTOWN_CSV_COLUMNS = [
  "Venue",
  "Country",
  "Address",
  "City",
  "Region",
  "Postal Code",
  "Start Date",
  "End Date",
  "Start Time",
  "End Time",
  // "Streaming Link" is left out on purpose (ART-128, under test): Bandsintown flagged our empty cell as "not a
  // valid URL" on an ordinary show, and we never fill it. Restore it here and below if they reject the file.
  "Ticket Link",
  "Ticket Type",
  "Ticket Link 2",
  "Ticket Type 2",
  "On-Sale Date",
  "On-Sale Time",
  "Lineup",
  "Event Name",
  "Description",
  "Scheduled date",
  "Scheduled time",
  "Timezone",
  "Artist Name",
  "Event Image",
] as const;

/** Bandsintown asks for at most this many events (rows) per file. */
export const BANDSINTOWN_MAX_ROWS = 25;

/** Countries where Bandsintown REQUIRES the region as a 2-letter state/province code. */
const REGION_REQUIRED = new Set(["US", "CA"]);

/**
 * What a show is missing that Bandsintown's template marks required (Venue, Country, City, Region for US/CA,
 * Start Date, Start Time, Timezone), in the words the export dialog prints. An empty list means the row can go.
 * Start Time is the show's DOORS time (PO ruling, ART-128) — not the first act's set.
 */
export function bandsintownMissing(show: ShowSummary): string[] {
  const missing: string[] = [];
  if (!show.venueName?.trim()) missing.push(show.kind === "festival" ? "festival" : "venue");
  if (!show.city?.trim()) missing.push("city");
  if (!show.country?.trim()) missing.push("country");
  else if (REGION_REQUIRED.has(countryCode(show.country)) && !show.state?.trim()) missing.push("state");
  if (!show.date) missing.push("date");
  if (!show.doorsTime) missing.push("doors time");
  if (!show.timezone?.trim()) missing.push("timezone");
  return missing;
}

/**
 * The lineup a show is exported with: the whole bill, with the band's own act first when the bill does not already
 * name it. ONE function because two things read it — the CSV's Lineup column, and the "check each lineup" list the
 * export shows afterwards, which has to name exactly the artists the file sent.
 */
export function bandsintownLineup(show: ShowSummary, ownActName?: string | null): string[] {
  const own = ownActName?.trim() ?? "";
  const bill = (show.bill ?? []).map((a) => a.artistName?.trim() ?? "").filter(Boolean);
  return own && !bill.some((name) => name.toLowerCase() === own.toLowerCase()) ? [own, ...bill] : bill;
}

/**
 * One CSV for the given shows, in Bandsintown's template. The Lineup carries the WHOLE bill including the band
 * itself (PO, ART-128): Bandsintown's docs call it "the other artists", but an upload without the band left it off
 * the event's lineup. `ownActName` goes first when the bill does not already name it.
 */
export function bandsintownCsv(shows: readonly ShowSummary[], ownActName?: string | null): string {
  const rows = shows.map((s) => {
    const lineup = bandsintownLineup(s, ownActName);
    const cells: Record<(typeof BANDSINTOWN_CSV_COLUMNS)[number], string> = {
      Venue: s.venueName ?? "",
      Country: countryName(s.country),
      Address: "",
      City: s.city ?? "",
      Region: s.state ?? "",
      "Postal Code": "",
      "Start Date": s.date ?? "",
      "End Date": "",
      // Doors (PO ruling), 24-hour HH:MM as the template requires — the wire carries "HH:mm:ss".
      "Start Time": (s.doorsTime ?? "").slice(0, 5),
      "End Time": "",
      "Ticket Link": s.ticketUrl ?? "",
      // Required whenever a ticket link is present; "Tickets" is the plain one of their five values.
      "Ticket Type": s.ticketUrl ? "Tickets" : "",
      "Ticket Link 2": "",
      "Ticket Type 2": "",
      "On-Sale Date": "",
      "On-Sale Time": "",
      Lineup: lineup.join(", "),
      // A festival's name IS its venueName on our record; a club show needs no event name.
      "Event Name": s.kind === "festival" ? (s.venueName ?? "") : "",
      Description: "",
      "Scheduled date": "",
      "Scheduled time": "",
      Timezone: s.timezone ?? "",
      "Artist Name": "",
      "Event Image": "",
    };
    return BANDSINTOWN_CSV_COLUMNS.map((c) => cells[c]);
  });
  return [[...BANDSINTOWN_CSV_COLUMNS], ...rows].map((r) => r.map(csvCell).join(",")).join("\r\n") + "\r\n";
}

function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function countryCode(country: string): string {
  const c = country.trim();
  if (/^[a-z]{2}$/i.test(c)) return c.toUpperCase();
  const lower = c.toLowerCase();
  if (lower === "united states" || lower === "usa" || lower === "united states of america") return "US";
  if (lower === "canada") return "CA";
  return c;
}

/** The country as a NAME — Bandsintown's template asks for "country of the venue", and a bare ISO code is
 *  ambiguous to a reader. A stored name passes through. */
function countryName(country: string | null | undefined): string {
  if (!country?.trim()) return "";
  const c = country.trim();
  if (!/^[a-z]{2}$/i.test(c)) return c;
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(c.toUpperCase()) ?? c;
  } catch {
    return c;
  }
}
